import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../api/services';
import client from '../api/client';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const handleUnauthorized = () => {
      // 業務邏輯：任何受保護 API 回 401 都代表目前登入狀態不可再信任，需立即清空前端 session。
      client.setToken(null);
      setUser(null);
      setError('登入狀態已失效，請重新登入');
      setLoading(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('tech-forum:auth-unauthorized', handleUnauthorized);
    }

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          client.setToken(token);
          const response = await authService.getMe();
          setUser(response.data);
        } catch (err) {
          localStorage.removeItem('token');
          client.setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('tech-forum:auth-unauthorized', handleUnauthorized);
      }
    };
  }, []);

  const login = async (account, password) => {
    try {
      setError(null);
      const response = await authService.login({ account, password });
      client.setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (account, name, email, password) => {
    try {
      setError(null);
      const response = await authService.register({ account, name, email, password });
      client.setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    client.setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await authService.getMe();
      setUser(response.data);
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  };

  const updatePassword = async (data) => {
    const response = await authService.updatePassword(data);
    if (response.data?.user) setUser(prev => ({ ...prev, ...response.data.user }));
    return response.data;
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    updatePassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

